/**
 * Student Context Hook
 * Calculates student progress stage for context-aware suggestions.
 */
import { useMemo } from 'react';

export type StudentStage = 'new' | 'beginner' | 'intermediate' | 'advanced';

export interface StudentContext {
  stage: StudentStage;
  completedCourses: string[];
  totalCourses: number;
}

interface CourseLike {
  code?: string;
  isPlaceholder?: boolean;
  [key: string]: unknown;
}

interface AcademicPlanLike {
  semesters?: CourseLike[][] | Record<string, CourseLike[]> | null;
  [key: string]: unknown;
}

/**
 * Hook for calculating student context/progress.
 */
export const useStudentContext = (
  academicPlan: AcademicPlanLike | null | undefined
): StudentContext => {
  return useMemo(() => {
    if (!academicPlan || !academicPlan.semesters) {
      return { stage: 'new', completedCourses: [], totalCourses: 0 };
    }

    const semesters = academicPlan.semesters;
    let completedCourses: string[] = [];

    if (Array.isArray(semesters)) {
      completedCourses = semesters
        .flat()
        .filter((course): course is CourseLike => !!course && !course.isPlaceholder)
        .map((course) => course.code)
        .filter((code): code is string => !!code);
    } else if (semesters && typeof semesters === 'object') {
      completedCourses = Object.values(semesters)
        .flat()
        .filter((course): course is CourseLike => !!course && !course.isPlaceholder)
        .map((course) => course.code)
        .filter((code): code is string => !!code);
    }

    const totalCourses = completedCourses.length;

    let stage: StudentStage = 'new';
    if (totalCourses >= 20) stage = 'advanced';
    else if (totalCourses >= 10) stage = 'intermediate';
    else if (totalCourses > 0) stage = 'beginner';

    return { stage, completedCourses, totalCourses };
  }, [academicPlan]);
};

/**
 * Get context-aware questions based on student stage.
 */
export const getContextAwareQuestions = (stage: StudentStage | string): string[] => {
  const baseQuestions = [
    'What courses can I take right now?',
    'What should I take next semester?',
    'Am I on track to graduate in 4 years?',
    'What does CMSC216 unlock for my degree?',
  ];

  const stageQuestions: Record<string, string[]> = {
    new: [
      'What courses should I start with as a CS major?',
      'Help me understand the CS degree requirements',
    ],
    beginner: [
      'What comes after CMSC131 and CMSC132?',
      'How should I choose my first electives?',
    ],
    intermediate: [
      'What 300-level courses should I prioritize?',
      'How do I choose my CS specialization area?',
    ],
    advanced: [
      'What 400-level courses fit my career goals?',
      'How can I optimize my remaining semesters?',
    ],
  };

  const contextQuestions = stageQuestions[stage] || [];
  return [...baseQuestions, ...contextQuestions.slice(0, 2)];
};
